import { formatNumber } from 'core/service/lib/number-format'
import Mustache from 'mustache'
import { buildHeaderTable } from 'core/service/pdf-generation/utils/builder'
import MESSAGES from 'core/data-model/constants/bi-messages'
import VALUES from 'core/data-model/constants/values'

import {
  getPensionTypeString,
  getPensionPercents,
  calculatePensionAmounts,
  getTotalPensionPeriod,
} from 'core/service/benefit-illustration/products/retirement'

const firstBenefitSummaryTable = (pensionType) => {
  return {
    title: Mustache.render(MESSAGES.RETIREMENT_BENEFITS, { pensionType: getPensionTypeString(pensionType) }),
    headers: [
      {
        text: MESSAGES.PERIOD_AGE,
        style: 'tableHeader',
      },
      {
        text: Mustache.render(MESSAGES.PENSION_TYPE, { pensionType: getPensionTypeString(pensionType) }),
        style: 'tableHeader',
      },
      {
        text: MESSAGES.PENSION_AMOUNT,
        style: 'tableHeader',
      },
    ],
  }
}

const buildFirstTotalBenefitSummary = (sumAssured, pensionType, basicPlan, policyValue) => {
  const productCode = basicPlan.basicPlanCode
  const pensionAge = basicPlan.pensionAge
  return [
    {
      text: Mustache.render(MESSAGES.TOTAL_BENEFIT_PENSION_AGE_UP_TO_95_PDF, {
        pensionAge,
      }),
      alignment: 'center',
      bold: true,
    },
    {
      text: Mustache.render(MESSAGES.BUMNAN_READY_MAXIMUM_PENSION_PERIOD, {
        totalPensionPeriod: getTotalPensionPeriod(pensionType, productCode, pensionAge),
      }),
      alignment: 'center',
      margin: [0, 8, 0, 0],
      bold: true,
    },
    {
      text: formatNumber(policyValue.subtotalPensionAmount, 2, true),
      alignment: 'center',
      margin: [0, 8, 0, 0],
      bold: true,
    },
  ]
}

const buildFirstBenefitSummary = (sumAssured, pensionType, basicPlan, policyValue) => {
  const productCode = basicPlan.basicPlanCode
  const pensionPercents = getPensionPercents(pensionType, productCode)
  const pensionAmounts = calculatePensionAmounts(sumAssured, pensionType, productCode)

  const benefitValuesBumnanSmart = [
    [
      {
        text: Mustache.render(MESSAGES.BENEFIT_PENSION_AGE_UP_TO_75, {
          pensionAge: basicPlan.pensionAge,
        }),
        alignment: 'center',
      },
      {
        text: Mustache.render(MESSAGES.PENSION_PERCENT, {
          pensionPercent: pensionPercents !== null ? pensionPercents.BUMNAN_SMART_AGE_UP_TO_75 : 0,
        }),
        alignment: 'center',
      },
      {
        text: formatNumber(pensionAmounts !== null ? pensionAmounts.BUMNAN_SMART_AGE_UP_TO_75 : 0, 2),
        alignment: 'center',
      },
    ],
    [
      {
        text: MESSAGES.BENEFIT_PENSION_AGE_76_80,
        alignment: 'center',
      },
      {
        text: Mustache.render(MESSAGES.PENSION_PERCENT, {
          pensionPercent: pensionPercents !== null ? pensionPercents.BUMNAN_SMART_AGE_76_TO_80 : 0,
        }),
        alignment: 'center',
      },
      {
        text: formatNumber(pensionAmounts !== null ? pensionAmounts.BUMNAN_SMART_AGE_76_TO_80 : 0, 2),
        alignment: 'center',
      },
    ],
    [
      {
        text: MESSAGES.BENEFIT_PENSION_AGE_81_85,
        alignment: 'center',
      },
      {
        text: Mustache.render(MESSAGES.PENSION_PERCENT, {
          pensionPercent: pensionPercents !== null ? pensionPercents.BUMNAN_SMART_AGE_81_TO_85 : 0,
        }),
        alignment: 'center',
      },
      {
        text: formatNumber(pensionAmounts !== null ? pensionAmounts.BUMNAN_SMART_AGE_81_TO_85 : 0, 2),
        alignment: 'center',
      },
    ],
    [
      {
        text: MESSAGES.BENEFIT_PENSION_AGE_86_95,
        alignment: 'center',
      },
      {
        text: Mustache.render(MESSAGES.PENSION_PERCENT, {
          pensionPercent: pensionPercents !== null ? pensionPercents.BUMNAN_SMART_AGE_86_TO_95 : 0,
        }),
        alignment: 'center',
      },
      {
        text: formatNumber(pensionAmounts !== null ? pensionAmounts.BUMNAN_SMART_AGE_86_TO_95 : 0, 2),
        alignment: 'center',
      },
    ],
  ]

  const benefitValues = benefitValuesBumnanSmart
  benefitValues.push(buildFirstTotalBenefitSummary(sumAssured, pensionType, basicPlan, policyValue))
  return benefitValues
}

const buildSecondBenefitSummary = (sumAssured, pensionType, basicPlan) => {
  const pensionAge = basicPlan.pensionAge
  return [
    [
      {
        text: Mustache.render(MESSAGES.BUMNAN_SMART_DEATH_BENEFIT_1, { pensionAge }),
        alignment: 'left',
        border: [true, true, true, false],
      },
      {
        text: MESSAGES.BUMNAN_SMART_DEATH_BENEFIT_1_1,
        alignment: 'left',
        border: [true, true, true, false],
      },
    ],
    [
      {
        text: '',
        alignment: 'left',
        border: [true, false, true, false],
      },
      {
        text: MESSAGES.BUMNAN_SMART_DEATH_BENEFIT_1_2,
        alignment: 'left',
        border: [true, false, true, false],
      },
    ],
    [
      {
        text: '',
        alignment: 'left',
        border: [true, false, true, false],
      },
      {
        text: MESSAGES.BUMNAN_SMART_DEATH_BENEFIT_1_DISCLAIMER,
        alignment: 'left',
        border: [true, false, true, true],
      },
    ],
    [
      {
        text: Mustache.render(MESSAGES.BUMNAN_SMART_DEATH_BENEFIT_2, { pensionAge, pensionAgePlus14: pensionAge + 14 }),
        alignment: 'left',
      },
      {
        text: MESSAGES.BUMNAN_SMART_DEATH_COVERAGE_AGE_UP_TO_79,
        alignment: 'left',
      },
    ],
    [
      {
        text: Mustache.render(MESSAGES.BUMNAN_SMART_DEATH_BENEFIT_3, { pensionAge, pensionAgePlus15: pensionAge + 15 }),
        alignment: 'left',
      },
      {
        text: MESSAGES.BUMNAN_SMART_DEATH_COVERAGE_AGE_AFTER_79,
        alignment: 'left',
      },
    ],
  ]
}

export const benefitSummarySectionBumnanSmart = ({ sumAssured, pensionType, basicPlan, policyValue }) => ({
  stack: [
    {
      text: firstBenefitSummaryTable(pensionType).title,
      style: 'title',
      pageBreak: 'before',
    },
    {
      text: MESSAGES.PENSION_AGE,
      bold: true,
    },
    {
      table: {
        headerRows: 1,
        widths: ['*', '*', '*'],
        margin: [100, 10, 10],
        body: [
          // $FlowFixMe
          buildHeaderTable(firstBenefitSummaryTable(pensionType).headers),
          ...buildFirstBenefitSummary(sumAssured, pensionType, basicPlan, policyValue),
        ],
      },
      layout: 'lightBorder',
      style: 'table',
    },
    {
      text: MESSAGES.RETIREMENT_DEATH_BENEFIT,
      bold: true,
    },
    {
      table: {
        headerRows: 1,
        widths: ['*', '*'],
        margin: [100, 10],
        body: [...buildSecondBenefitSummary(sumAssured, pensionType, basicPlan)],
      },
      layout: 'lightBorder',
      style: 'table',
    },
  ],
  style: 'groupRow',
})
